import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { deleteAccount } from '../services/userService';

interface DeleteAccountModalProps {
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ onClose, onDeleted }) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setError('');
    setLoading(true);
    try {
      await deleteAccount();
      onDeleted();
    } catch (e: any) {
      if (e?.code === 'auth/requires-recent-login') {
        setError('Please sign out and sign back in, then try again.');
      } else {
        setError('Account deletion failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-base-100 dark:bg-dark-200 p-6 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-500">
            <Trash2 size={20} />
          </div>
          <button
            onClick={onClose}
            className="text-base-content/40 dark:text-dark-content/40 hover:text-base-content dark:hover:text-dark-content transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <h3 className="text-lg font-bold text-base-content dark:text-dark-content mb-2">Delete Account</h3>
        <p className="text-sm text-base-content/60 dark:text-dark-content/60 mb-4">
          This will permanently delete your account, all transcripts, and all data. Type{' '}
          <strong className="text-red-500 font-mono">DELETE</strong> to confirm.
        </p>

        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE to confirm"
          className="w-full px-4 py-2.5 rounded-xl border border-base-300 dark:border-dark-300 bg-base-100 dark:bg-dark-300 text-base-content dark:text-dark-content text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
        />

        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-base-300 dark:border-dark-300 font-semibold text-base-content dark:text-dark-content hover:bg-base-200 dark:hover:bg-dark-300 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={confirmText !== 'DELETE' || loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            {loading ? 'Deleting…' : 'Delete Forever'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
